import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ManualStatusSetter from './manual-status-setter';


interface RollResult {
  success: boolean;
  value: number;
  consequence: string;
}

type MoraleState = 'Mutinous' | 'Resentful' | 'Unnerved' | 'Discouraged' | 'Normal' | 'Heartened';
type CleanlinessState = 'Hazardous' | 'Neglected' | 'Standard' | 'Pristine';
type NavigationState = 'On course' | 'Off course' | 'Lost at sea';

interface SeafaringTurnTrackerProps {
  onMoraleChange: (morale: MoraleState) => void;
  onCleanlinessChange: (cleanliness: CleanlinessState) => void;
  onNavigationChange: (navigation: NavigationState) => void;
  currentMorale?: MoraleState;
  currentCleanliness?: CleanlinessState;
  currentNavigation?: NavigationState;
}

interface SeafaringTurnState {
  weatherPrediction: RollResult;
  navigation: RollResult;
  morale: {
    currentState: MoraleState;
    rollResult: RollResult;
  };
  cleanliness: {
    currentState: CleanlinessState;
    rollResult: RollResult;
  };
  navigationState: NavigationState;
}

const initialState: SeafaringTurnState = {
  weatherPrediction: { success: false, value: 0, consequence: '' },
  navigation: { success: false, value: 0, consequence: '' },
  morale: {
    currentState: 'Normal',
    rollResult: { success: false, value: 0, consequence: '' }
  },
  cleanliness: {
    currentState: 'Standard',
    rollResult: { success: false, value: 0, consequence: '' }
  },
  navigationState: 'On course'
};

const moraleStates: MoraleState[] = ['Mutinous', 'Resentful', 'Unnerved', 'Discouraged', 'Normal', 'Heartened'];
const cleanlinessStates: CleanlinessState[] = ['Hazardous', 'Neglected', 'Standard', 'Pristine'];

const navigationCircumstances = [
  { label: 'Very familiar coast in sight', dc: 5 },
  { label: 'Coast studied carefully in sight', dc: 8 },
  { label: 'Coast seen once in sight', dc: 13 },
  { label: 'Unknown coast in sight', dc: 15 },
  { label: 'Open ocean, clear weather', dc: 17 },
  { label: 'Open ocean, poor visibility', dc: 25 },
  { label: 'Open ocean, stormy weather', dc: 30 },
  { label: 'Open ocean, gale or hurricane', dc: 40 },
];

const SeafaringTurnTracker: React.FC<SeafaringTurnTrackerProps> = ({ 
  onMoraleChange, 
  onCleanlinessChange, 
  onNavigationChange,
  currentMorale,
  currentCleanliness,
  currentNavigation
}) => {
  const [turnState, setTurnState] = useState<SeafaringTurnState>(() => ({
    ...initialState,
    morale: {
      ...initialState.morale,
      currentState: currentMorale || initialState.morale.currentState
    },
    cleanliness: {
      ...initialState.cleanliness,
      currentState: currentCleanliness || initialState.cleanliness.currentState
    },
    navigationState: currentNavigation || initialState.navigationState
  }));
  
  const [consecutiveNavigationFailures, setConsecutiveNavigationFailures] = useState<number>(0);
  const [tempRolls, setTempRolls] = useState<{weatherPrediction: number, navigation: number, morale: number, cleanliness: number}>({
    weatherPrediction: 0,
    navigation: 0,
    morale: 0,
    cleanliness: 0
  });
  const [selectedNavigationCircumstance, setSelectedNavigationCircumstance] = useState(navigationCircumstances[0]);

  // Update state when props change
  useEffect(() => {
    if (currentMorale !== undefined && currentMorale !== turnState.morale.currentState) {
      setTurnState(prev => ({
        ...prev,
        morale: {
          ...prev.morale,
          currentState: currentMorale
        }
      }));
    }
  }, [currentMorale, turnState.morale.currentState]);

  useEffect(() => {
    if (currentCleanliness !== undefined && currentCleanliness !== turnState.cleanliness.currentState) {
      setTurnState(prev => ({
        ...prev,
        cleanliness: {
          ...prev.cleanliness,
          currentState: currentCleanliness
        }
      }));
    }
  }, [currentCleanliness, turnState.cleanliness.currentState]);

  useEffect(() => {
    if (currentNavigation !== undefined && currentNavigation !== turnState.navigationState) {
      setTurnState(prev => ({
        ...prev,
        navigationState: currentNavigation
      }));
    }
  }, [currentNavigation, turnState.navigationState]);

  const getMoraleEffect = (state: MoraleState): string => {
    switch (state) {
      case 'Heartened': return '+1 morale bonus on Will saves against fear effects while on the ship';
      case 'Normal': return 'No bonuses or penalties';
      case 'Discouraged': return 'The rally check is made at a -2 penalty';
      case 'Unnerved': return 'The crew has the shaken condition while on the ship';
      case 'Resentful': return 'The crew has the shaken condition while on the ship and the captain rolls twice on all Profession (Sailor) and Captain checks and takes the lower result';
      case 'Mutinous': return 'The crew mutinies';
      default: return '';
    }
  };

  const getCleanlinessEffect = (state: CleanlinessState): string => {
    switch (state) {
      case 'Pristine': return '+1 bonus to all Profession (Sailor) checks while on the ship';
      case 'Standard': return 'No bonuses or penalties';
      case 'Neglected': return '-1 penalty to all Profession (Sailor) checks while on the ship. 10% chance of disease outbreak';
      case 'Hazardous': return '-2 penalty to all Profession (Sailor) checks while on the ship. 50% chance of disease outbreak';
      default: return '';
    }
  };

  const handleSetState = (type: 'weatherPrediction' | 'navigation' | 'morale' | 'cleanliness') => {
    const value = tempRolls[type];
    let dc = 15; // Default DC
    let success = false;
    let consequence = '';
  
    switch (type) {
      case 'weatherPrediction':
        success = value >= dc;
        if (!success) {
          consequence = value <= dc - 5
            ? 'Roll twice on all Profession (Sailor) checks for heavy winds or storms and take the lower result.'
            : '-2 penalty on all Profession (Sailor) checks for heavy winds or storms.';
        }
        setTurnState(prev => ({ ...prev, [type]: { success, value, consequence } }));
        break;
  
      case 'navigation':
        dc = selectedNavigationCircumstance.dc + (consecutiveNavigationFailures > 0 ? (consecutiveNavigationFailures === 1 ? 2 : 5) : 0);
        success = value >= dc;
        let newNavigationState: NavigationState = turnState.navigationState;
        if (success) {
          consequence = 'Navigation successful.';
          setConsecutiveNavigationFailures(0);
          newNavigationState = 'On course';
        } else {
          const newFailures = consecutiveNavigationFailures + 1;
          setConsecutiveNavigationFailures(newFailures);
          if (newFailures >= 3) {
            consequence = 'Lost at sea!';
            newNavigationState = 'Lost at sea';
          } else {
            consequence = `Navigation failed. ${newFailures} consecutive failure(s).`;
            newNavigationState = 'Off course';
          }
        }
        setTurnState(prev => ({
          ...prev,
          navigation: { success, value, consequence },
          navigationState: newNavigationState
        }));
        onNavigationChange(newNavigationState);
        break;
  
      case 'morale':
        success = value >= dc;
        const currentMoraleIndex = moraleStates.indexOf(turnState.morale.currentState);
        const moraleDiff = Math.floor((value - dc) / 5);
        const newMoraleIndex = Math.max(0, Math.min(currentMoraleIndex + moraleDiff, moraleStates.length - 1));
        const newMoraleState = moraleStates[newMoraleIndex];
        consequence = `Crew morale is now ${newMoraleState}.`;
        setTurnState(prev => ({
          ...prev,
          morale: {
            currentState: newMoraleState,
            rollResult: { success, value, consequence }
          }
        }));
        onMoraleChange(newMoraleState);
        break;
  
      case 'cleanliness':
        success = value >= dc;
        const currentCleanlinessIndex = cleanlinessStates.indexOf(turnState.cleanliness.currentState);
        const cleanlinessDiff = Math.floor((value - dc) / 5);
        const newCleanlinessIndex = Math.max(0, Math.min(currentCleanlinessIndex + cleanlinessDiff, cleanlinessStates.length - 1));
        const newCleanlinessState = cleanlinessStates[newCleanlinessIndex];
        consequence = `Ship cleanliness is now ${newCleanlinessState}.`;
        setTurnState(prev => ({
          ...prev,
          cleanliness: {
            currentState: newCleanlinessState,
            rollResult: { success, value, consequence }
          }
        }));
        onCleanlinessChange(newCleanlinessState);
        break;
    }
  };

  const handleUpdateMorale = (newMorale: MoraleState) => {
    setTurnState(prev => ({
      ...prev,
      morale: {
        ...prev.morale,
        currentState: newMorale,
      },
    }));
    onMoraleChange(newMorale);
  };

  const handleUpdateCleanliness = (newCleanliness: CleanlinessState) => {
    setTurnState(prev => ({
      ...prev,
      cleanliness: {
        ...prev.cleanliness,
        currentState: newCleanliness,
      },
    }));
    onCleanlinessChange(newCleanliness);
  };

  const handleUpdateNavigation = (newNavigation: NavigationState) => {
    setTurnState(prev => ({
      ...prev,
      navigationState: newNavigation,
    }));
    onNavigationChange(newNavigation);
  };

  return (
    <div className="space-y-4">
      <Card className="w-full max-w-md mx-auto mt-4">
        <CardHeader>
          <CardTitle>Seafaring Turn Tracker</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(['weatherPrediction', 'navigation'] as const).map((type) => (
              <div key={type} className="space-y-2">
                <Label htmlFor={type}>
                  {type === 'weatherPrediction' ? 'Weather Prediction' : type.charAt(0).toUpperCase() + type.slice(1)} Roll
                </Label>
                {type === 'navigation' && (
                  <Select
                    value={selectedNavigationCircumstance.label}
                    onValueChange={(value) => setSelectedNavigationCircumstance(navigationCircumstances.find(c => c.label === value) || navigationCircumstances[0])}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select navigation circumstance" />
                    </SelectTrigger>
                    <SelectContent>
                      {navigationCircumstances.map((circumstance) => (
                        <SelectItem key={circumstance.label} value={circumstance.label}>
                          {circumstance.label} (DC {circumstance.dc})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex space-x-2">
                  <Input
                    id={type}
                    type="number"
                    placeholder="Roll result"
                    onChange={(e) => setTempRolls(prev => ({ ...prev, [type]: parseInt(e.target.value) || 0 }))}
                  />
                  <Button onClick={() => handleSetState(type)}>Update</Button>
                </div>
                {turnState[type].value > 0 && (
                  <div className={`p-2 rounded ${
                    turnState[type].success 
                      ? 'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100' 
                      : 'bg-red-100 dark:bg-red-900 text-red-900 dark:text-red-100'
                  }`}>
                    Result: {turnState[type].value} ({turnState[type].success ? 'Success' : 'Failure'})
                    <p className="text-sm mt-1">{turnState[type].consequence}</p>
                  </div>
                )}
              </div>
            ))}
            <div className="space-y-2">
              <Label>Consecutive Navigation Failures</Label>
              <div className="flex space-x-2">
                {[1, 2, 3].map((num) => (
                  <Checkbox
                    key={num}
                    checked={consecutiveNavigationFailures >= num}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setConsecutiveNavigationFailures(num);
                      } else if (num === consecutiveNavigationFailures) {
                        setConsecutiveNavigationFailures(num - 1);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
            {(['morale', 'cleanliness'] as const).map((type) => (
              <div key={type} className="space-y-2">
                <Label htmlFor={type}>{type.charAt(0).toUpperCase() + type.slice(1)} Roll</Label>
                <div className="flex space-x-2">
                  <Input
                    id={type}
                    type="number"
                    placeholder="Roll result"
                    onChange={(e) => setTempRolls(prev => ({ ...prev, [type]: parseInt(e.target.value) || 0 }))}
                  />
                  <Button onClick={() => handleSetState(type)}>Update</Button>
                </div>
                <div>Current {type}: {turnState[type].currentState}</div>
                <div className="text-sm italic">
                  Effect: {type === 'morale' ? getMoraleEffect(turnState[type].currentState) : getCleanlinessEffect(turnState[type].currentState)}
                </div>
                {turnState[type].rollResult.value > 0 && (
                  <div className={`p-2 rounded ${
                    turnState[type].rollResult.success 
                      ? 'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100' 
                      : 'bg-red-100 dark:bg-red-900 text-red-900 dark:text-red-100'
                  }`}>
                    Result: {turnState[type].rollResult.value} ({turnState[type].rollResult.success ? 'Success' : 'Failure'})
                    <p className="text-sm mt-1">{turnState[type].rollResult.consequence}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <ManualStatusSetter 
        onUpdateMorale={handleUpdateMorale}
        onUpdateCleanliness={handleUpdateCleanliness}
        onUpdateNavigation={handleUpdateNavigation}
        currentMorale={turnState.morale.currentState}
        currentCleanliness={turnState.cleanliness.currentState}
        currentNavigation={turnState.navigationState}
      />
    </div>
  );
};

export default SeafaringTurnTracker;